import { useEffect } from "react"
import Value from "aldoo-ra/Value"
import { SetUserAttribution } from "../api"

// localStorage keys. FIRST is written once (first-touch); LAST is overwritten
// on every campaign visit (last-touch). FLUSHED tracks what we've already sent
// per user so we only POST when the snapshot actually changed.
const STORE_FIRST = "aldoo-attribution-first"
const STORE_LAST = "aldoo-attribution-last"
const STORE_FLUSHED = "aldoo-attribution-flushed"

// Presence of any of these in the URL triggers a capture. utm_* are the
// campaign tags; gclid/fbclid are the Google/Meta click ids.
const UTM_KEYS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
  "gclid", "fbclid",
]

// Build a touch snapshot from the current URL, or null if no campaign signal.
function snapshotFromURL() {
  const params = new URLSearchParams(window.location.search)
  const data = {}
  let hasUTM = false
  for (const k of UTM_KEYS) {
    const v = params.get(k)
    if (v) {
      data[k] = v
      hasUTM = true
    }
  }
  if (!hasUTM) return null

  data.referrer = document.referrer || ""
  data.landing_page = window.location.pathname || "/"
  data.captured_at = new Date().toISOString()
  return data
}

// Record the touch: last-touch overwrites every time; first-touch is written
// only once and never replaced.
function capture(given = null) {
  if (typeof window === "undefined") return
  try {
    const snap = given || snapshotFromURL()
    if (!snap) return

    const json = JSON.stringify(snap)
    localStorage.setItem(STORE_LAST, json) // last-touch — always freshest
    if (!localStorage.getItem(STORE_FIRST)) {
      localStorage.setItem(STORE_FIRST, json) // first-touch — write once
    }
  } catch {
    // storage blocked / private mode — attribution is best-effort, ignore
  }
}

/**
 * A touch that did not come through the URL — a click on one of the site's
 * own ad placements, which navigates with clean links: { utm_source,
 * utm_medium, utm_campaign, utm_content }.
 */
export function recordTouch(params) {
  if (typeof window === "undefined" || !params) return
  const data = {}
  for (const k of UTM_KEYS) if (params[k]) data[k] = String(params[k])
  if (!Object.keys(data).length) return
  capture({ ...data, referrer: document.referrer || "", landing_page: window.location.pathname || "/", captured_at: new Date().toISOString() })
}

/**
 * Marketing attribution capture. Mount once at app boot (alongside
 * useOTPLogin). Two phases:
 *
 *   1. On every load, snapshot utm_* / click ids / referrer / landing into
 *      localStorage — first-touch written once, last-touch overwritten each
 *      campaign visit (works before the visitor is known).
 *   2. Once authenticated, flush { first, last } to the user endpoint so it
 *      lands on user.attribution. The server keeps first-touch immutable and
 *      refreshes last-touch. We only POST when the snapshot changed since the
 *      last successful flush for this user.
 *
 * Enables revenue attribution to either the acquiring channel (first) or the
 * converting channel (last) — for partner reporting + channel effectiveness.
 */
export default function useUTMCapture() {
  const [auth] = Value("user-credentials")

  // Capture as early as possible, independent of auth.
  useEffect(() => {
    capture()
  }, [])

  // Flush to the server when authenticated and the snapshot has changed.
  useEffect(() => {
    if (typeof window === "undefined") return
    if (!auth?.auth_token || !auth?._id) return

    let first, last
    try {
      first = localStorage.getItem(STORE_FIRST)
      last = localStorage.getItem(STORE_LAST)
    } catch {
      return
    }
    if (!first && !last) return

    // Identity of the current state — last-touch captured_at is the part that
    // moves; fall back to first when there's no last yet.
    const marker = last || first
    const flushedKey = `${STORE_FLUSHED}:${auth._id}`
    try {
      if (localStorage.getItem(flushedKey) === marker) return // nothing new
    } catch {
      /* ignore */
    }

    ;(async () => {
      try {
        const data = {}
        if (first) data.first = JSON.parse(first)
        if (last) data.last = JSON.parse(last)

        const res = await SetUserAttribution(data)
        if (res?.result) {
          try {
            localStorage.setItem(flushedKey, marker)
          } catch {
            /* ignore */
          }
        }
      } catch (error) {
        console.warn("[UTM capture] flush failed", error)
      }
    })()
  }, [auth])
}
